import { SplitBillService } from 'services/split-bill-service';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import apiUrlProvider, { RESULT } from 'constants/route-paths';
import { multiply, plus } from 'utils/number';
import { useAsyncError } from 'utils/hooks';
import { requestReceipt, requestLoyalty } from 'services/payment';
import { qrStand as qrType } from 'constants/payment';
import apiUrlProviderEvent, { GET_EVENTS } from 'constants/event-api-endpoint';
import { SplitBillRemaining } from 'services/repository/SplitBillRepository';
import { SESSION } from 'constants/enum/session';
import { SSE_TYPE } from 'constants/enum/see-type';

const splitPaymentService = new SplitBillService();
const useViewModel = () => {
  const token = sessionStorage?.getItem(SESSION.TOKEN) || '';
  const {
    location: { state: data },
    push,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useHistory<any>();
  const [remaining, setRemaining] = useState<SplitBillRemaining>();
  const throwError = useAsyncError();
  const paymentInfo = data.paymentInfo;
  const [open, setOpen] = useState(false);
  const [transfer, setTransfer] = useState();
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const water = document.getElementById('water');
    if (water) {
      water.style.transform =
        'translate(0' +
        ',' +
        `${remaining ? 100 - (remaining?.count * 100) / remaining?.total : 100}` +
        '%)';

      if (allPaid) {
        handleConfirm();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining]);

  const allPaid = useMemo(() => remaining?.count === remaining?.total, [remaining]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleEventSource = () => {
    const url = apiUrlProviderEvent.get(GET_EVENTS, { tid: paymentInfo.placingPayment.pid });
    const eventSource = new EventSource(`${url}?publish=all`);

    const handleMessage = (event: MessageEvent) => {
      const parsedData = JSON.parse(event.data);

      if (parsedData?.message?.type === SSE_TYPE.NOTI) {
        setRemaining(parsedData?.message);
      } else if (
        parsedData?.message?.type === SSE_TYPE.STATUS &&
        parsedData?.message?.splitPID !== paymentInfo.pid
      ) {
        setTransfer(parsedData?.message);
        if (parsedData?.message.confirm) {
          handleReceipt();
        }
      }
    };
    eventSource.onmessage = handleMessage;

    return eventSource;
  };

  const handleRemaining = () => {
    getRemainingAmount().then((initRemainingItem) => {
      initRemainingItem && setRemaining(initRemainingItem);
    });
  };

  useEffect(() => {
    if (paymentInfo) {
      handleRemaining();
      handleEventSource();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subTotalAmount = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return paymentInfo?.payload?.checkoutItems.reduce((sum: number, item: any) => {
      return plus(sum, item.totalPrice);
    }, 0);
  }, [paymentInfo?.payload?.checkoutItems]);

  const totalAmountExcludedFee = useCallback(() => {
    return paymentInfo.amountExcludedFee || paymentInfo.amountExcludedFee === 0
      ? subTotalAmount
      : paymentInfo.fullAmount;
  }, [paymentInfo.amountExcludedFee, paymentInfo.fullAmount, subTotalAmount]);

  const handleReceipt = () => {
    sessionStorage?.removeItem(SESSION.HEAD_SPLIT_TOKEN);
    setConfirming(false);

    const { paymentData, redirect_url } = data;
    const { type, taxAmount, extraFee } = paymentInfo;
    Promise.all([requestReceipt(token), requestLoyalty(token)])
      .then(([receiptRes, loyaltyRes]) => {
        const {
          id,
          amount,
          cashbackTo,
          cashbackAmount,
          qrStand,
          date,
          loyaltyReward,
          reviewEngine,
          logoImage,
          companyName,
          address,
          tip_amount,
          charge_amount,
          payload,
          tip_percentage,
          discount,
          splitBill,
        } = receiptRes;

        const receipt = {
          pin: qrStand?.pin,
          id,
          cashbackTo,
          amount,
          cashbackAmount,
          image: logoImage,
          companyName: type === qrType ? qrStand.name : companyName,
          companyAddress: address,
          createdAt: date,
          description: qrStand?.description,
          paymentData,
          loyalties: loyaltyRes,
          loyaltyReward,
          reviewEngine,
          successUrl: redirect_url ?? null,
          failUrl: redirect_url ?? null,
          tip_amount,
          charge_amount,
          payload,
          totalTipPercent: multiply(tip_percentage, 100),
          taxAmount: Number(taxAmount),
          paymentType: type,
          discount,
          extraFee,
          subTotal: totalAmountExcludedFee(),
          type: 'success',
          splitBill: {
            splitAmount: remaining?.total,
            ...splitBill,
          },
        };

        push(apiUrlProvider.getForRoute(RESULT), receipt);
      })
      .catch(() => {
        throwError();
      });
  };

  const handleConfirm = () => {
    setConfirming(true);
    if (paymentInfo.pid === remaining?.pid) {
      splitPaymentService.confirm(token);
    }
  };

  const getRemainingAmount = async () => {
    return token && splitPaymentService.getRemaining(token);
  };

  return {
    transfer,
    remaining,
    handleClose,
    open,
    handleOpen,
    getRemainingAmount,
    paymentInfo,
    confirming,
    allPaid,
  };
};

export default useViewModel;
